import React, { useMemo, useState } from 'react';
import { ExpandIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { galleryImages, type GalleryCategory } from '../data/images';
import { trackEvent } from '../utils/analytics';
import { Reveal } from './Reveal';
import { Lightbox } from './Lightbox';
type FilterId = 'all' | GalleryCategory;
const FILTERS: {
  id: FilterId;
  en: string;
  es: string;
}[] = [{
  id: 'all',
  en: 'All',
  es: 'Todas'
}, {
  id: 'living',
  en: 'Living',
  es: 'Sala'
}, {
  id: 'kitchen',
  en: 'Kitchen',
  es: 'Cocina'
}, {
  id: 'bedrooms',
  en: 'Bedrooms',
  es: 'Habitaciones'
}, {
  id: 'bathrooms',
  en: 'Bathrooms',
  es: 'Baños'
}, {
  id: 'waterView',
  en: 'Water View',
  es: 'Vista al Agua'
}, {
  id: 'amenities',
  en: 'Amenities',
  es: 'Amenidades'
}];
export function GallerySection() {
  const {
    lang,
    t
  } = useLanguage();
  const [filter, setFilter] = useState<FilterId>('all');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const images = useMemo(() => filter === 'all' ? galleryImages : galleryImages.filter(image => image.categories.includes(filter)), [filter]);
  const openLightbox = (index: number) => {
    setOpenIndex(index);
    trackEvent('gallery_open', {
      category: filter,
      position: index + 1
    });
  };
  const featured = images[0];
  const supporting = images.slice(1);
  return <section id="gallery" className="w-full bg-ivory py-20 lg:py-28" data-id="element-229" data-mp="1cx59x0538ibi">
      <div className="mx-auto max-w-editorial px-5 lg:px-8" data-id="element-230" data-mp="1czj6xejbw4lp">
        <Reveal data-id="element-231" data-mp="17zmyn33llr0c">
          <p className="label-caps text-gold" data-id="element-232" data-mp="17z3sejnb8r25">
            {t('Explore the Property', 'Conoce la Propiedad')}
          </p>
          <h2 className="mt-5 max-w-[16ch] font-display text-[34px] font-semibold uppercase leading-[1.02] text-midnight sm:text-[44px] lg:text-[52px]" data-id="element-233" data-mp="13591av5dl9t1">
            {t('Take a Look Inside', 'Descubre Cada Espacio')}
          </h2>
          <div className="gold-rule mt-7 w-full" data-id="element-234" data-mp="1qwo5ktgpq16x" />
        </Reveal>

        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3" role="tablist" aria-label={t('Photo categories', 'Categorías de fotos')} data-id="element-235" data-mp="1qvd1stn9otft">
          {FILTERS.map(item => <button key={item.id} type="button" role="tab" aria-selected={filter === item.id} onClick={() => setFilter(item.id)} className={`label-caps border-b pb-1 transition-colors duration-150 ease-out ${filter === item.id ? 'border-gold text-midnight' : 'border-transparent text-ink/55 hover:text-midnight'}`} data-id="element-236" data-mp="3q3d0xnliks1u">
              {lang === 'en' ? item.en : item.es}
            </button>)}
        </div>

        {featured && <div className="mt-10 grid gap-4 lg:grid-cols-12" data-id="element-237" data-mp="2xfpj2gbmofiw">
            <Reveal className="lg:col-span-8" data-id="element-238" data-mp="qoribppsefec">
              <button type="button" onClick={() => openLightbox(0)} className="group relative block h-[300px] w-full overflow-hidden sm:h-[420px] lg:h-[560px]" data-id="element-239" data-mp="qmwt3z7hg14g">
                <img src={featured.src} alt={lang === 'en' ? featured.altEn : featured.altEs} width={featured.width} height={featured.height} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-editorial group-hover:scale-[1.02]" style={{
              objectPosition: featured.objectPosition
            }} data-id="element-240" data-mp="2b7br34sf0al7" />
                <span className="absolute bottom-4 right-4 flex items-center gap-2 bg-midnight/80 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-white" data-id="element-241" data-mp="38uk5ygxwule8">
                  <ExpandIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-242" data-mp="33wimhuwkok28" />
                  {t('View', 'Ver')}
                </span>
              </button>
            </Reveal>

            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-4 lg:grid-cols-1" data-id="element-243" data-mp="19izut1p83vou">
              {supporting.slice(0, 2).map((image, position) => <Reveal key={image.id} delay={0.05 * (position + 1)} data-id="element-244" data-mp="1fxyhle5xiu9w" data-mpi={position}>
                  <button type="button" onClick={() => openLightbox(position + 1)} className="group block h-[180px] w-full overflow-hidden sm:h-[200px] lg:h-[272px]" data-id="element-245" data-mp="1g01emi5i1no4">
                    <img src={image.src} alt={lang === 'en' ? image.altEn : image.altEs} width={image.width} height={image.height} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-editorial group-hover:scale-[1.02]" style={{
                objectPosition: image.objectPosition
              }} data-id="element-246" data-mp="s8vx7yjwunbp" />
                  </button>
                </Reveal>)}
            </div>
          </div>}

        {supporting.length > 2 && <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-id="element-247" data-mp="2rowbx56mlea7">
            {supporting.slice(2).map((image, position) => <Reveal key={image.id} delay={Math.min(position * 0.03, 0.15)} data-id="element-248" data-mp="2iphlu6udf5q8" data-mpi={position}>
                <button type="button" onClick={() => openLightbox(position + 3)} className="group block h-[132px] w-full overflow-hidden sm:h-[160px] lg:h-[190px]" data-id="element-249" data-mp="2drzh6sndzco1">
                  <img src={image.src} alt={lang === 'en' ? image.altEn : image.altEs} width={image.width} height={image.height} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-editorial group-hover:scale-[1.02]" style={{
              objectPosition: image.objectPosition
            }} data-id="element-250" data-mp="2403hjd5njehp" />
                </button>
              </Reveal>)}
          </div>}
      </div>

      {openIndex !== null && <Lightbox images={images} index={openIndex} onIndexChange={setOpenIndex} onClose={() => setOpenIndex(null)} data-id="element-251" data-mp="12ehy3j6zspb4" />}
    </section>;
}